
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface PerformanceYearContextType {
  activeYear: string;
  setActiveYear: (year: string) => Promise<void>;
  isLoading: boolean;
}

const PerformanceYearContext = createContext<PerformanceYearContextType | undefined>(undefined);

export const usePerformanceYear = () => {
  const context = useContext(PerformanceYearContext);
  if (!context) {
    throw new Error('usePerformanceYear must be used within a PerformanceYearProvider');
  }
  return context;
};

interface PerformanceYearProviderProps {
  children: ReactNode;
}

export const PerformanceYearProvider: React.FC<PerformanceYearProviderProps> = ({ children }) => {
  const [activeYear, setActiveYearState] = useState<string>('FY25');
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Fetch the current active year from performance_stage table
  useEffect(() => {
    const fetchActiveYear = async () => {
      try {
        const { data, error } = await supabase
          .from('performance_stage')
          .select('year')
          .limit(1)
          .single();

        if (error) {
          console.error('Error fetching active year:', error);
          return;
        }

        if (data?.year) {
          setActiveYearState(data.year);
        }
      } catch (error) {
        console.error('Error fetching active year:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActiveYear();
  }, []);

  const setActiveYear = async (year: string) => {
    try {
      // Update performance_stage table
      const { error } = await supabase
        .from('performance_stage')
        .update({ year })
        .eq('id', (await supabase.from('performance_stage').select('id').limit(1).single()).data?.id);

      if (error) {
        console.error('Error updating active year:', error);
        throw error;
      }

      // Update local state
      setActiveYearState(year);

      // Invalidate all performance-related queries to refetch with new year
      queryClient.invalidateQueries({ queryKey: ['performance-goals'] });
      queryClient.invalidateQueries({ queryKey: ['performance-appraisals'] });
      queryClient.invalidateQueries({ queryKey: ['team-member-appraisals'] });
      queryClient.invalidateQueries({ queryKey: ['performance-bands'] });
      queryClient.invalidateQueries({ queryKey: ['team-member-bands'] });
      queryClient.invalidateQueries({ queryKey: ['band-disagreements'] });
      queryClient.invalidateQueries({ queryKey: ['banding-simulation'] });
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    } catch (error) {
      console.error('Error setting active year:', error);
      throw error;
    }
  };

  return (
    <PerformanceYearContext.Provider value={{ activeYear, setActiveYear, isLoading }}>
      {children}
    </PerformanceYearContext.Provider>
  );
};
